/**
 * DemoLimitModal
 * 데모 한도(프로젝트/설비) 도달 시 표시되는 토스 스타일 센터 모달
 *
 * 디자인 철학: 토스 — 텍스트와 여백만으로 모든 것을 말한다.
 * - 장식 최소화, 타이포그래피 웨이트 대비로 시각 계층
 * - 숫자를 시각적 앵커로 (토스 잔액 표시 패턴)
 * - createPortal 패턴 (ConfirmDialog.tsx 참조)
 * - Capacitor Browser.open()으로 외부 브라우저 열기
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { WEB_URL } from "@/lib/constants/app";
import { openExternalUrl } from "@/lib/utils/browser";
import "@/styles/OnboardingPage.css";

export interface DemoLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: "project" | "machine";
  currentCount?: number;
  maxCount?: number;
}

export function DemoLimitModal({
  isOpen,
  onClose,
  limitType,
  currentCount,
  maxCount,
}: DemoLimitModalProps) {
  const navigate = useNavigate();

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const rafIdRef = useRef<number | null>(null);

  const handleCloseTopModal = useCallback(
    (e: Event) => {
      e.preventDefault();
      onClose();
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    let closeTimer: ReturnType<typeof setTimeout> | undefined;

    if (isOpen) {
      setIsVisible(true);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("closeTopModal", handleCloseTopModal);
    } else {
      setIsAnimating(false);
      closeTimer = setTimeout(() => setIsVisible(false), 200);
    }

    return () => {
      if (closeTimer) clearTimeout(closeTimer);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("closeTopModal", handleCloseTopModal);
    };
  }, [isOpen, handleKeyDown, handleCloseTopModal]);

  if (!isVisible) return null;

  const isProject = limitType === "project";

  const handleGoToSettings = () => {
    onClose();
    navigate("/settings");
  };

  const handleOpenWeb = async () => {
    await openExternalUrl(`${WEB_URL}/settings`);
    onClose();
  };

  return createPortal(
    <div
      className={`onboarding-page fixed inset-0 z-50 flex items-center justify-center px-6 transition-colors duration-200 ${
        isAnimating ? "bg-black/40" : "bg-black/0"
      }`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-limit-title"
    >
      <div
        className={`w-full max-w-[300px] transition-all duration-200 ease-out ${
          isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-[0.97]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: "var(--color-card)",
            borderRadius: "24px",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* 닫기 버튼 */}
          <div className="flex justify-end px-4 pt-4">
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-full"
              style={{ color: "var(--color-tertiary)", background: "none", border: "none" }}
              onClick={onClose}
              aria-label="닫기"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* 콘텐츠 — 넉넉한 여백, 텍스트가 주인공 */}
          <div className="px-8 pt-2 pb-3">
            {/* 제목 — 토스 스타일: 짧고 굵게, 한 줄 */}
            <h3
              id="demo-limit-title"
              className="text-[20px] font-bold leading-snug"
              style={{
                color: "var(--color-primary)",
                letterSpacing: "-0.02em",
              }}
            >
              {isProject ? "무료 프로젝트를 모두 썼어요" : "무료 설비 등록을 모두 썼어요"}
            </h3>

            {/* 사용량 — 토스 잔액 패턴: 큰 숫자가 시각적 앵커 */}
            {currentCount != null && maxCount != null && (
              <div className="mt-6 flex items-baseline">
                <span
                  className="tabular-nums leading-none"
                  style={{
                    fontSize: "40px",
                    fontWeight: 700,
                    color: "var(--color-primary)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {currentCount}
                </span>
                <span
                  className="tabular-nums leading-none"
                  style={{
                    fontSize: "40px",
                    fontWeight: 700,
                    color: "var(--color-tertiary)",
                    letterSpacing: "-0.03em",
                  }}
                >
                  /{maxCount}
                </span>
                <span
                  className="ml-2 text-[14px] font-medium"
                  style={{ color: "var(--color-secondary)" }}
                >
                  {isProject ? "프로젝트" : "설비"}
                </span>
              </div>
            )}

            {/* 설명 — 조용하게, 충분한 간격 뒤에 */}
            <p
              className="mt-6 text-[14px] leading-relaxed"
              style={{
                color: "var(--color-secondary)",
                fontWeight: 400,
              }}
            >
              회사를 연결하거나
              <br />
              웹에서 플랜을 시작해 보세요
            </p>
          </div>

          {/* 버튼 — 토스: 풀폭, 무게감 차이로 구분 */}
          <div className="px-5 pt-4 pb-5 flex flex-col gap-2">
            <button type="button" className="ob-btn w-full" onClick={handleGoToSettings}>
              회사 연결하기
            </button>

            <button
              type="button"
              className="w-full flex items-center justify-center gap-1 rounded-xl py-3.5 text-[15px]"
              style={{
                color: "var(--color-secondary)",
                fontWeight: 500,
                background: "var(--color-surface)",
                border: "none",
              }}
              onClick={handleOpenWeb}
            >
              웹에서 플랜 등록
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.5 }}
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DemoLimitModal;
